import { MarketNotReady } from './MarketScopeTabs';
import { useMarketReady } from './marketScope';
import { useState } from 'react';
import { Badge, Card, EmptyState, ErrorState, Spinner, Table, Td, Th } from '@/components/ui';
import { formatKrwPlain } from '@/lib/format';
import { TradingNav } from './TradingNav';
import { StockDetailModal } from './StockDetailModal';
import {
  PriceVolumeTh,
  QuoteCell,
  StackedSortTh,
  StockIdentity,
  SWING_COLUMN_NOTE,
  SwingCell,
} from './components';
import { formatVolume } from './formatVolume';
import { quoteColorClass, resolveQuote, type QuoteView } from './priceDisplay';
import { sortRows, type SortState } from './tableSort';
import {
  useLeadingSectors,
  useLeadingStocks,
  usePrices,
} from './useStocks';
import type { LeadingStock } from './stockTypes';
import { LEADING_STOCK_LIMIT } from './stockApi';

/**
 * 주도주. (V128, docs/impl/113)
 *
 * 관리자가 고른 섹터의 거래대금 상위 LEADING_STOCK_LIMIT 종목을 보여준다(✏️ 2026-10-09 50 → 100 · 사용자 결정 · stockApi.ts).
 * 모든 계정이 같은 목록을 본다 - 개인 관심종목과 별개다.
 *
 * 🔴 왜 관심종목 카테고리가 아닌가
 *   카테고리로 만들면 개인 상한(50개)을 함께 쓴다. 사용자가 원한 것은
 *   "내 관심종목과 무관하게 주도 섹터를 훑고, 그중 담고 싶은 것만 담는" 흐름이라
 *   목록을 따로 두고 각 줄에 [담기] 를 붙였다.
 *   ✏️ 2026-10-05 사용자 요청으로 [관심](담기) 열을 뺐다("별로 필요가 없다") - 담기는 종목 창의 [관심종목에 담기]로 한다.
 *   그 자리에 왕복성 3개월 / 6개월 열이 들어갔다.
 *
 * 🔴 기본 정렬이 거래대금이 아니다
 *   상위 N(LEADING_STOCK_LIMIT)을 자르는 기준은 거래대금인데, 화면에 그리는 순서는
 *   코스피 → 코스닥, 그 안에서 현재가 내림차순이다 (2026-09-10 사용자 확정).
 *   자르는 기준과 보는 순서는 다른 문제다 - 무엇을 넣을지는 거래가 정하고,
 *   눈으로 훑을 때는 시장별로 묶여 있는 편이 읽힌다.
 *
 * 🔴 정렬 방식을 하루 만에 바꿨다 (2026-09-10 저녁 사용자 요청)
 *   처음에는 카드 오른쪽 위에 [기본][현재가][거래대금] 버튼을 뒀는데,
 *   섹터 화면은 열 제목을 누르는 방식이라 같은 표를 두 가지로 조작하게 됐다.
 *   섹터 쪽으로 맞췄다 - 열 제목을 누르면 오름 → 내림 → 기본으로 돈다.
 */

/**
 * 정렬할 수 있는 열. 기본 순서(코스피 → 코스닥, 현재가 내림차순)는 key 가 null 일 때다.
 *
 * 🔴 거래대금이 없다 (2026-09-11 사용자 요청 - "거래대금 열 없애자")
 *   목록에 들어갈 종목(LEADING_STOCK_LIMIT)을 자르는 기준은 여전히 거래대금이다. 다만 화면에서는
 *   뺐다 - 열이 다섯이면 폰에서 표가 가로로 밀린다. 자르는 기준이 무엇인지는
 *   물음표 안내와 표 아래 각주가 말한다.
 */
type LeadingSortKey = 'price' | 'volume' | 'changeAmount' | 'changeRate' | 'swing3m' | 'swing6m';

export function LeadingPage() {
  const sectors = useLeadingSectors();
  const stocks = useLeadingStocks();
  const [openStockId, setOpenStockId] = useState<number | null>(null);
  const [sort, setSort] = useState<SortState<LeadingSortKey>>({ key: null, dir: 'asc' });
  const [helpOpen, setHelpOpen] = useState(false);
  const ready = useMarketReady();

  /*
    🔴 국내 전용 화면이라 탭에서는 숨지만 주소로 직접 들어올 수 있다 (2026-09-20)
      전에는 안내를 배너로 얹고 국내 주도주를 그대로 그렸다. 미국을 고른 상태에서
      국내 숫자를 보여주면 어느 시장의 것인지 헷갈린다. 미국 업종 분류가 생기면
      KR_ONLY_PATHS 와 isMarketReady 를 고치면 이 분기가 사라진다 (I-67).
   */
  if (!ready) {
    return (
      <div className="space-y-4">
        <TradingNav />
        {/* 🔴 막힌 이유가 계좌가 아니라 섹터 분류다 (MarketNotReady 주석) */}
        <MarketNotReady reason="주도 섹터는 섹터 분류 위에서 고르는데, 미국 종목은 주도주 · 섹터 화면이 쓰는 업종 분류가 아직 없습니다. 미국 스윙 계좌의 다섯 섹터는 그 계좌만 쓰는 손분류라 여기에는 나오지 않습니다." />
      </div>
    );
  }

  /*
    실시간 시세. (2026-09-10 저녁 사용자 요청 - "섹터탭 페이지처럼 전날 대비와 등락률")

    이 화면은 처음에 마지막 일봉의 종가만 보여줬다. 섹터 화면과 같은 훅을 붙여
    현재가·대비·등락률을 같은 규칙으로 맞춘다 - 같은 종목이 화면마다 다른 값으로
    보이면 어느 쪽을 믿어야 할지 알 수 없다.

    거래량·거래대금은 여전히 마지막 일봉의 값이다. 토스 현재가 API 에 그 둘이 없다.
  */
  const list = stocks.data ?? [];
  const prices = usePrices(list.map((stock) => stock.stockId));
  const stale = prices.data?.[0]?.stale ?? true;

  /*
    정렬 전에 시세를 행에 합쳐둔다 - 화면에 보이는 값과 정렬에 쓰는 값이 같아야 한다.
    (섹터 화면과 같은 이유. 따로 계산하면 "현재가 순으로 눌렀는데 종가 행만
    이상한 자리에 있는" 표가 된다)
  */
  const rows = list.map((stock) => ({
    stock,
    view: resolveQuote(
      prices.data?.find((price) => price.stockId === stock.stockId),
      stock.lastClose,
      // 주도주 목록은 직전 종가를 따로 주지 않는다. 등락은 시세 응답이 계산해 온 값이다
      null,
    ),
  }));

  const sorted = sortRows(
    rows,
    sort,
    (row, key) => {
      switch (key) {
        case 'price':
          return row.view.price;
        case 'volume':
          return row.stock.lastVolume;
        case 'changeAmount':
          // 🔴 금액과 %는 정렬 결과가 다르다. 누른 이름의 값으로 줄 세운다
          return row.view.change;
        case 'swing3m':
          return row.stock.swing3m;
        case 'swing6m':
          return row.stock.swing6m;
        default:
          return row.view.changeRate;
      }
    },
    defaultOrder,
  );

  return (
    <div className="space-y-4">
      <TradingNav />

      <header>
        <div className="flex items-center gap-2">
          <h1 className="text-xl font-semibold">주도주</h1>
          <button
            type="button"
            onClick={() => setHelpOpen(!helpOpen)}
            aria-label="이 목록이 무엇인지 설명"
            className="flex h-6 w-6 items-center justify-center rounded-full bg-brand-weak text-xs font-semibold text-brand transition-colors hover:bg-brand hover:text-brand-fg"
          >
            ?
          </button>
        </div>
        <p className="mt-1 text-sm text-fg-muted">
          지금 시장을 이끄는 섹터에서 거래가 가장 활발한 종목입니다 (최대 {LEADING_STOCK_LIMIT}개, 분류된
          종목이 적으면 그보다 적게 나옵니다). 열 제목을 눌러 정렬할 수 있고, 담고 싶은
          종목은 눌러서 나오는 창의 [관심종목에 담기]로 넣습니다.
        </p>

        {/*
          물음표 안내. (2026-09-10 사용자 요청)
          접어 두는 이유는 매번 읽을 내용이 아니어서다 - 처음 한 번과
          "왜 거래대금이지?" 가 떠올랐을 때만 필요하다.
        */}
        {helpOpen && (
          <div className="mt-2 space-y-2 rounded-lg bg-surface-muted px-3 py-3 text-xs leading-relaxed text-fg-muted">
            <p>
              <strong className="text-fg">누가 정하나</strong> — 주도 섹터는 지금
              코드(마이그레이션)로만 지정합니다. 화면에서 바꾸는 기능은 아직 없습니다.
              모든 계정이 같은 목록을 보며, 개인 관심종목과는 별개입니다.
              종목을 눌러 나오는 창에서 [관심종목에 담기]를 눌러야 내 관심종목에 들어갑니다.
            </p>
            <p>
              <strong className="text-fg">왜 거래대금 순인가</strong> — 단타에서는
              "얼마나 큰 회사인가"보다 "얼마나 활발히 거래되나"가 더 맞는 기준이기
              때문입니다 — 거래가 얕으면 원하는 가격에 못 붙습니다.
            </p>
            <p>
              <strong className="text-fg">종목은 고정이 아닙니다</strong> — 섹터만 지정해
              두고 종목은 화면을 열 때마다 그 섹터의 거래대금 상위를 뽑습니다.
              기준은 <strong>마지막 일봉</strong>의 거래대금이라 장중에는 바뀌지 않고,
              그날 일봉이 들어온 뒤 바뀝니다 — 코스피는 15:40에 잠정 봉으로 한 번, 20:25 확정 봉으로
              한 번 더, 코스닥은 20:30에 바뀝니다.
            </p>
            {sectors.data && sectors.data.length > 0 && (
              <div className="space-y-1 border-t border-line pt-2">
                {sectors.data.map((sector) => (
                  <p key={sector.id}>
                    <strong className="text-fg">{sector.sectorCode}</strong>
                    {sector.note && ` — ${sector.note}`}
                  </p>
                ))}
              </div>
            )}
          </div>
        )}

        {/* 지정된 섹터 배지. 무엇이 주도로 잡혀 있는지 한눈에 */}
        {sectors.data && sectors.data.length > 0 && (
          <div className="mt-2 flex flex-wrap gap-1">
            {sectors.data.map((sector) => (
              <Badge key={sector.id} tone="info">
                {sector.sectorCode}
              </Badge>
            ))}
          </div>
        )}
      </header>

      {(sectors.isLoading || stocks.isLoading) && <Spinner label="주도주를 불러오는 중" />}
      {stocks.error && (
        <ErrorState message={stocks.error.message} onRetry={() => void stocks.refetch()} />
      )}

      {stocks.data && stocks.data.length === 0 && (
        <Card>
          <EmptyState
            title="아직 지정된 주도 섹터가 없습니다"
            /*
              🔴 2026-09-20 — "관리자 화면에서 섹터를 지정하면" 이라고 적혀 있었다.
                그런 화면도 API 도 없다. LeadingSectorController 는 GET 둘뿐이고,
                지금 섹터 둘은 마이그레이션(V128)으로 박은 값이다.
                찾을 수 없는 조작을 지시하면 사용자가 관리자 메뉴를 뒤지게 된다.
            */
            description="주도 섹터는 아직 코드(마이그레이션)로만 지정할 수 있습니다. 화면에서 바꾸는 기능은 준비 중입니다."
          />
        </Card>
      )}

      {stocks.data && stocks.data.length > 0 && (
        <Card title={`주도주 ${stocks.data.length}종목`}>
          <Table>
            <thead>
              <tr>
                <Th>종목</Th>
                {/*
                  🔴 2026-10-05 사용자 요청 — [관심] 열을 뺐다("별로 필요가 없다") · 제목을 두 줄로 쌓아 폭을 줄이고
                    그 자리에 왕복성 3개월 / 6개월 열을 넣었다(폰에서 열 수는 그대로 넷)
                */}
                <PriceVolumeTh
                  priceKey="price"
                  volumeKey="volume"
                  sort={sort}
                  onSort={setSort}
                  stale={stale}
                  twoLine
                />
                <StackedSortTh
                  topLabel="대비"
                  topKey="changeAmount"
                  bottomLabel="등락률"
                  bottomKey="changeRate"
                  sort={sort}
                  onSort={setSort}
                  twoLine
                />
                <StackedSortTh
                  topLabel="왕복 3개월"
                  topKey="swing3m"
                  bottomLabel="6개월"
                  bottomKey="swing6m"
                  sort={sort}
                  onSort={setSort}
                  twoLine
                />
              </tr>
            </thead>
            <tbody>
              {sorted.map((row) => (
                <LeadingRow
                  key={row.stock.stockId}
                  stock={row.stock}
                  view={row.view}
                  onOpen={() => setOpenStockId(row.stock.stockId)}
                />
              ))}
            </tbody>
          </Table>
          <p className="mt-2 text-xs text-fg-subtle">
            열 제목을 누르면 그 열로 정렬됩니다 (오름 → 내림 → 기본). 기본 순서는 코스피
            → 코스닥, 그 안에서 현재가가 높은 순입니다. 목록에 들어갈 종목(최대 {LEADING_STOCK_LIMIT}개)은 화면에
            보이지 않는 거래대금으로 고릅니다 — 왜 그 기준인지는 제목 옆 물음표에
            적어뒀습니다.
          </p>
          <p className="mt-1 text-xs text-fg-subtle">
            현재가·대비·등락률은 15초마다 갱신되며 장이 닫혀 있으면 마지막 종가입니다.
            거래량은 마지막 일봉의 값이라 실시간이 아닙니다.
          </p>
          <p className="mt-1 text-xs text-fg-subtle">{SWING_COLUMN_NOTE}</p>
        </Card>
      )}

      <StockDetailModal stockId={openStockId} onClose={() => setOpenStockId(null)} />
    </div>
  );
}

/**
 * 기본 순서. (2026-09-10 사용자 확정)
 *
 * 코스피 → 코스닥, 그 안에서 현재가 내림차순이다.
 * 어느 열도 안 눌렀을 때와, 누른 열의 값이 같을 때 이 순서로 간다.
 *
 * 🔴 종가가 아니라 화면에 보이는 값(view.price)으로 줄 세운다
 *   장중에는 시세가, 마감 뒤에는 마지막 종가가 들어 있다. 종가로 줄 세우면
 *   장중에 "현재가가 높은 순" 이라고 적어 놓고 어제 값으로 정렬하게 된다.
 *
 * 값이 없는 종목(일봉이 오래 멈춤)은 언제나 맨 뒤다 - 0 으로 취급해 섞으면
 * "값싼 종목" 처럼 보인다.
 */
function defaultOrder(
  a: { stock: LeadingStock; view: QuoteView },
  b: { stock: LeadingStock; view: QuoteView },
): number {
  const marketDiff = marketRank(a.stock.market) - marketRank(b.stock.market);
  return marketDiff !== 0 ? marketDiff : desc(a.view.price, b.view.price);
}

/** 코스피가 0, 코스닥이 1, 나머지는 뒤. 숫자가 작을수록 앞이다. */
function marketRank(market: string | null): number {
  if (market === 'KOSPI') return 0;
  if (market === 'KOSDAQ') return 1;
  return 2;
}

function desc(a: number | null, b: number | null): number {
  if (a === null && b === null) return 0;
  if (a === null) return 1;
  if (b === null) return -1;
  return b - a;
}

function LeadingRow({
  stock,
  view,
  onOpen,
}: {
  stock: LeadingStock;
  /** 현재가·대비·등락률과 그 색. 시세가 없으면 마지막 종가로 채워져 온다 */
  view: QuoteView;
  onOpen: () => void;
}) {
  return (
    <tr className="transition-colors hover:bg-surface-hover">
      <Td>
        <button type="button" onClick={onOpen} className="text-left">
          <div className="flex items-baseline gap-2">
            <span className="font-medium">{stock.name}</span>
          </div>
          <StockIdentity market={stock.market} symbol={stock.symbol} />
          {/* 어느 주도 섹터인지. 둘 다일 수 있다 */}
          <p className="truncate text-xs text-fg-subtle">{stock.sectorCodes.join(' · ')}</p>
          {stock.universe && (
            <div className="mt-1">
              <Badge tone="info">
                <span className="text-[10px]">선별 대상</span>
              </Badge>
            </div>
          )}
        </button>
      </Td>
      <Td align="right" numeric>
        {/* 색은 전일 대비 방향이다. 마감 뒤에도 유지한다 (priceDisplay.quoteColorClass) */}
        <span className={quoteColorClass(view)}>
          {view.price === null ? '-' : formatKrwPlain(view.price)}
        </span>
        {stock.lastVolume !== null && (
          <span className="block text-[11px] leading-tight text-fg-subtle">
            {formatVolume(stock.lastVolume)}
          </span>
        )}
      </Td>
      <Td align="right" numeric>
        {/* 🔴 현재가는 앞 열이 이미 찍었다. 여기서 또 그리면 한 줄에 두 번 나온다 */}
        <QuoteCell view={view} showPrice={false} stacked />
      </Td>
      {/*
        왕복성 3개월 / 6개월 (2026-10-05 사용자 요청 · 1년 환산 - SwingCell 주석)
        🔴 [관심](담기) 칸은 같은 날 뺐다 - 담기는 종목을 눌러 나오는 창의 [관심종목에 담기]로 한다
      */}
      <Td align="right" numeric>
        <SwingCell threeMonth={stock.swing3m} sixMonth={stock.swing6m} />
      </Td>
    </tr>
  );
}
